import { type LiFiQuoteResponse } from "@umami/swap";
import { formatEther } from "ethers";

/**
 * Parses a LiFi quote to extract detailed fee breakdown information
 * @param lifiQuote - The LiFi quote response containing route and step information
 * @returns Object containing network costs and provider fees breakdown, or null if parsing fails
 */
export const parseLiFiFeeBreakdown = (lifiQuote: LiFiQuoteResponse) => {
  const breakdown: {
    networkCosts: Array<{ symbol: string; amount: string; usdValue: number }>;
    providerFees: Array<{ name: string; amount: string; symbol: string; usdValue: number }>;
    totalNetworkCostUSD: number;
    totalProviderFeesUSD: number;
  } = {
    networkCosts: [],
    providerFees: [],
    totalNetworkCostUSD: 0,
    totalProviderFeesUSD: 0,
  };

  lifiQuote.route.steps.forEach(step => {
    if (step.estimate.gasCosts) {
      step.estimate.gasCosts.forEach(gasCost => {
        const amount = formatEther(gasCost.amount || "0");
        const usdValue = parseFloat(gasCost.amountUSD || "0");
        const symbol = gasCost.token.symbol || "Unknown";

        breakdown.networkCosts.push({
          symbol,
          amount: parseFloat(amount).toFixed(6),
          usdValue,
        });
        breakdown.totalNetworkCostUSD += usdValue;
      });
    }

    if (step.estimate.feeCosts) {
      step.estimate.feeCosts
        .filter(feeCost => !feeCost.included)
        .forEach(feeCost => {
          const amount = formatEther(feeCost.amount || "0");
          const usdValue = parseFloat(feeCost.amountUSD || "0");
          const symbol = feeCost.token.symbol || "Unknown";

          breakdown.providerFees.push({
            name: feeCost.name || "Provider Fee",
            amount: parseFloat(amount).toFixed(6),
            symbol,
            usdValue,
          });
          breakdown.totalProviderFeesUSD += usdValue;
        });
    }
  });

  return breakdown;
};

/**
 * Formats the fee breakdown into a user-friendly tooltip text
 * @param breakdown - The fee breakdown object returned by parseLiFiFeeBreakdown
 * @returns Formatted string showing network costs and provider fees
 */
export const formatLiFiFeeBreakdown = (breakdown: ReturnType<typeof parseLiFiFeeBreakdown>) => {
  const lines: string[] = [];

  // Network costs (gas costs from each step)
  if (breakdown.networkCosts.length > 0) {
    const networkCostsText = breakdown.networkCosts
      .map(cost => {
        const usdDisplay = cost.usdValue < 0.01 ? "<$0.01" : `$${cost.usdValue.toFixed(2)}`;

        return `${usdDisplay} (${cost.amount} ${cost.symbol})`;
      })
      .join(", ");
    lines.push(`Network cost ${networkCostsText}`);
  }

  // Provider fees (bridge fees, protocol fees, etc.)
  if (breakdown.providerFees.length > 0) {
    if (lines.length > 0) {
      lines.push("");
    }
    const providerFeesText = breakdown.providerFees
      .map(fee => {
        const usdDisplay = fee.usdValue < 0.01 ? "<$0.01" : `$${fee.usdValue.toFixed(2)}`;

        return `${usdDisplay} (${fee.amount} ${fee.symbol})`;
      })
      .join(", ");
    lines.push(`Provider fee ${providerFeesText}`);
  }

  return lines.join("\n");
};
